// ── coach-cutoff: the Week-3 add/import gate (FR-PART-002, blueprint §6 gate #4)
// The cutoff is enforced by the coaching API before participant writes. The
// local date calculation mirrors that authoritative rule so the UI can close
// the affordance early; a server `CO_CUTOFF_EXCEEDED` refusal remains the final
// authority for stale data, races, and direct requests.
//
// The API returns HTTP 410 for `CO_CUTOFF_EXCEEDED` (see
// coaching-shell-session-shared.ts). The detector below is shared by create and
// bulk-import forms so a deliberate cutoff refusal never becomes generic outage
// copy or a false success.
//
// Super Admin / Admin are NEVER cutoff-gated (they add late joiners by design).
// The cutoff applies to COACH add/import only and only within an ASSIGNED cohort;
// the API enforces both the role/scope and this Week-3 rule.

import { ApiClientError } from "../../../services/api-client";
import type { CoachingRole } from "../../../domain/roles";

/** The program week (1-based) after which a coach may no longer add/import. */
export const COACH_ADD_CUTOFF_WEEK = 3;
/** Days per program week (10-session weekly cadence). */
const DAYS_PER_WEEK = 7;

export type CutoffState =
  // The role is not cutoff-gated at all (SA/Admin) — add/import always allowed.
  | "not-applicable"
  // Coach, within the cutoff window — add/import allowed.
  | "open"
  // Coach, past Week 3 — add/import disabled by the authoritative cutoff.
  | "closed"
  // Coach, but the cohort start date is unknown so elapsed weeks can't be
  // computed — keep the local affordance open; the server still decides.
  | "unknown";

export interface CutoffDecision {
  state: CutoffState;
  /** 1-based program week the cohort is currently in, or null when unknown. */
  currentWeek: number | null;
  /** Whether the coach add/import affordance may render/enable. */
  allowAddImport: boolean;
  /** Honest, non-leaking copy describing the gate. */
  reason: string;
  /** Whether the API enforces this cutoff for the affected role/scope. */
  serverEnforced: true;
}

/** Whole program weeks elapsed since `startDate` as of `now` (0 on day one). */
export function elapsedProgramWeeks(startDate: string | null | undefined, now: Date = new Date()): number | null {
  if (!startDate) return null;
  // start_date is a date-only YYYY-MM-DD (blueprint §1). Parse at UTC midnight to
  // avoid a local-tz off-by-one; compare against the UTC date of `now`.
  const start = Date.parse(`${startDate}T00:00:00Z`);
  if (Number.isNaN(start)) return null;
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const days = Math.floor((today - start) / (24 * 60 * 60 * 1000));
  if (days < 0) return 0; // cohort hasn't started yet → week 1, fully open.
  return Math.floor(days / DAYS_PER_WEEK);
}

/**
 * Resolve the coach add/import cutoff for a role + cohort start date. SA/Admin
 * are never gated; a coach is gated by elapsed weeks vs COACH_ADD_CUTOFF_WEEK.
 * The API independently enforces this rule and returns HTTP 410 with
 * CO_CUTOFF_EXCEEDED when a stale client attempts a late add/import.
 */
export function resolveCoachCutoff(
  role: CoachingRole,
  startDate: string | null | undefined,
  now: Date = new Date(),
): CutoffDecision {
  if (role === "super_admin" || role === "admin") {
    return {
      state: "not-applicable",
      currentWeek: null,
      allowAddImport: true,
      reason: "Admins can add participants at any point in the program.",
      serverEnforced: true,
    };
  }

  const elapsed = elapsedProgramWeeks(startDate, now);
  if (elapsed === null) {
    // No start date → cannot compute the local affordance. Do NOT fabricate a
    // closed gate; leave it open and let the server remain authoritative.
    return {
      state: "unknown",
      currentWeek: null,
      allowAddImport: true,
      reason: "The cohort start date isn't set, so the Week 3 add cutoff can't be checked here.",
      serverEnforced: true,
    };
  }

  // currentWeek is 1-based: 0 elapsed weeks → week 1. The cutoff closes once the
  // current week exceeds COACH_ADD_CUTOFF_WEEK (i.e. from week 4 onward).
  const currentWeek = elapsed + 1;
  const closed = currentWeek > COACH_ADD_CUTOFF_WEEK;
  if (closed) {
    return {
      state: "closed",
      currentWeek,
      allowAddImport: false,
      reason:
        `This cohort is in week ${currentWeek}. Coaches can add participants only through week ` +
        `${COACH_ADD_CUTOFF_WEEK}; a super admin can still add a late joiner.`,
      serverEnforced: true,
    };
  }
  return {
    state: "open",
    currentWeek,
    allowAddImport: true,
    reason: `This cohort is in week ${currentWeek} — within the week ${COACH_ADD_CUTOFF_WEEK} add window.`,
    serverEnforced: true,
  };
}

// ── Server CUTOFF_EXCEEDED recognition ───────────────────────────────────────
// The API's canonical response is HTTP 410 + CO_CUTOFF_EXCEEDED. Message
// matching remains defensive for older envelopes/proxies that preserve only
// the text.
const CUTOFF_MESSAGE = /cutoff|week\s*3|too late to add|add window (?:has )?closed/i;

export function isCutoffExceededError(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) return false;
  const code = (error.code ?? "").toUpperCase();
  if (code === "CUTOFF_EXCEEDED" || code === "CO_CUTOFF_EXCEEDED") return true;
  if (error.status === 409 || error.status === 423) return CUTOFF_MESSAGE.test(error.message ?? "");
  return CUTOFF_MESSAGE.test(error.message ?? "");
}
